import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plug, RefreshCw, Trash2 } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { StatusPill } from "@/components/system";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const STATUS_PILL: Record<string, { status: "ok" | "warn" | "alert" | "offline"; label: string }> =
  {
    connected: { status: "ok", label: "Connected" },
    unverified: { status: "warn", label: "Not verified" },
    error: { status: "alert", label: "Error" },
  };

export function HomeyConnectionSection() {
  const utils = trpc.useUtils();
  const { data: connection } = trpc.homey.getConnection.useQuery();

  const [name, setName] = useState("My Homey");
  const [address, setAddress] = useState("");
  const [token, setToken] = useState("");

  useEffect(() => {
    if (connection) {
      setName(connection.name);
      setAddress(connection.address);
    }
  }, [connection]);

  const save = trpc.homey.saveConnection.useMutation({
    onSuccess: ({ validation }) => {
      void utils.homey.getConnection.invalidate();
      setToken("");
      if (validation.ok) toast.success("Connected to Homey");
      else toast.error(`Saved, but connection failed: ${validation.error}`);
    },
    onError: (e) => toast.error(e.message),
  });

  const test = trpc.homey.testConnection.useMutation({
    onSuccess: (result) => {
      void utils.homey.getConnection.invalidate();
      if (result.ok) toast.success("Homey is reachable");
      else toast.error(result.error ?? "Connection failed");
    },
    onError: (e) => toast.error(e.message),
  });

  const remove = trpc.homey.deleteConnection.useMutation({
    onSuccess: () => {
      void utils.homey.getConnection.invalidate();
      setAddress("");
      setToken("");
      toast.success("Connection removed");
    },
    onError: (e) => toast.error(e.message),
  });

  const busy = save.isPending || test.isPending || remove.isPending;
  const pill = connection ? STATUS_PILL[connection.status] : undefined;

  return (
    <Card data-slot="homey-connection-section">
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2">
            <Plug className="size-4" /> Homey connection
          </CardTitle>
          {pill && <StatusPill status={pill.status}>{pill.label}</StatusPill>}
        </div>
        <CardDescription>
          Connect your Homey Pro on the local network. Create an API key in the Homey Web App under
          Settings, then API Keys, with device, flow, zone, insights and system scopes.
          {connection && ` Current token: ${connection.tokenPreview}`}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {connection?.status === "error" && connection.last_error && (
          <p className="text-sm text-destructive">{connection.last_error}</p>
        )}
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="homey-name">Name</Label>
            <Input id="homey-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="homey-address">Address</Label>
            <Input
              id="homey-address"
              placeholder="http://192.168.1.x"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5 sm:col-span-2">
            <Label htmlFor="homey-token">API key</Label>
            <Input
              id="homey-token"
              type="password"
              placeholder={
                connection ? "Enter a new key to replace the stored one" : "Personal Access Token"
              }
              value={token}
              onChange={(e) => setToken(e.target.value)}
            />
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            disabled={busy || !address || (!token && !connection)}
            onClick={() => {
              if (token) {
                save.mutate({ name, address, token });
              } else {
                test.mutate();
              }
            }}
          >
            {busy ? <Loader2 className="animate-spin" /> : <RefreshCw />}
            {token || !connection ? "Save and test" : "Test connection"}
          </Button>
          {connection && (
            <Button variant="outline" disabled={busy} onClick={() => remove.mutate()}>
              <Trash2 /> Remove
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
